// shabba-dashboard/src/components/analytics/StalenessIndicator.tsx

import React, { useEffect, useState } from 'react';
import { parseMsFromTs, toEAT } from '../../utils/time';
import { Clock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import type { StalenessData } from '../../types';

interface StalenessIndicatorProps {
  data: StalenessData;
  baselineTimestamp?: number; // fallback baseline if no lastUpdateTimestamp provided
  lastUpdateTimestamp?: string; // raw timestamp from Order Book Changes for selected symbol
}

export function StalenessIndicator({ data, baselineTimestamp, lastUpdateTimestamp }: StalenessIndicatorProps) {
  const { lastUpdateAge, lastTradeAge, isStale, stalenessLevel } = data;
  const detail = data.lastTradeDetail;

  // Helpers for FIX/ISO timestamps (mirrors logic in BookChanges)
  const parseTimestampToMs = (ts?: string): number => parseMsFromTs(ts);

  // Live elapsed seconds computed from the last update timestamp from Order Book Changes
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const tsMs = parseTimestampToMs(lastUpdateTimestamp);
    const base = tsMs > 0 ? tsMs : (typeof baselineTimestamp === 'number' && isFinite(baselineTimestamp) ? baselineTimestamp : Date.now());
    setElapsed(Math.max(0, (Date.now() - base) / 1000));
    const id = setInterval(() => {
      setElapsed(Math.max(0, (Date.now() - base) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [baselineTimestamp, lastUpdateTimestamp]);

  // Prefer live elapsed from last update timestamp; fall back to analytics-provided age + elapsed
  const computedLastUpdateAge = (() => {
    const tsMs = parseTimestampToMs(lastUpdateTimestamp);
    if (tsMs > 0) return Math.max(0, (Date.now() - tsMs) / 1000);
    return Math.max(0, lastUpdateAge + elapsed);
  })();

  const getStalenessIcon = () => {
    switch (stalenessLevel) {
      case 'fresh': return <CheckCircle size={16} style={{ color: '#10b981' }} />;
      case 'aging': return <Clock size={16} style={{ color: '#f59e0b' }} />;
      case 'stale': return <AlertTriangle size={16} style={{ color: '#f59e0b' }} />;
      case 'very_stale': return <XCircle size={16} style={{ color: '#ef4444' }} />;
      default: return <Clock size={16} style={{ color: '#94a3b8' }} />;
    }
  };

  const getStalenessColor = () => {
    switch (stalenessLevel) {
      case 'fresh': return '#10b981';
      case 'aging': return '#f59e0b';
      case 'stale': return '#f59e0b';
      case 'very_stale': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getStalenessStatus = () => {
    switch (stalenessLevel) {
      case 'fresh': return 'good';
      case 'aging': return 'warning';
      case 'stale': return 'warning';
      case 'very_stale': return 'danger';
      default: return 'neutral';
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(0)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        {getStalenessIcon()}
        <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
          Data Staleness
        </h4>
      </div>

      {/* Staleness Level Indicator */}
      <div style={{ 
        padding: '8px 12px', 
        backgroundColor: getStalenessColor() + '20',
        borderRadius: '6px',
        border: `1px solid ${getStalenessColor()}40`,
        marginBottom: '12px'
      }}>
        <div style={{ 
          fontSize: '12px', 
          fontWeight: '600', 
          color: getStalenessColor(),
          textAlign: 'center',
          textTransform: 'uppercase'
        }}>
          {stalenessLevel.replace('_', ' ')}
        </div>
      </div>

      {/* Time Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
            Last Update
          </div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: getStalenessColor()
          }}>
            {formatTime(computedLastUpdateAge)}
          </div>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>
            {computedLastUpdateAge < 60 ? 'Recent' : computedLastUpdateAge < 300 ? 'Aging' : 'Stale'}
          </div>
          {lastUpdateTimestamp && (
            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
              at {toEAT(lastUpdateTimestamp)}
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
            Last Trade (Filled)
          </div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: detail ? getStalenessColor() : '#94a3b8'
          }}>
            {detail ? `${detail.side.toUpperCase()} ${detail.size.toLocaleString()} @ ${detail.price.toFixed(2)}` : 'N/A'}
          </div>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>
            {lastTradeAge > 0 ? (lastTradeAge < 300 ? 'Active' : 'Quiet') : 'No trades'}
          </div>
        </div>
      </div>

      {/* Staleness Warning */}
      {isStale && (
        <div style={{ 
          marginTop: '12px', 
          padding: '8px 12px', 
          backgroundColor: '#ef444420',
          borderRadius: '4px',
          border: '1px solid #ef444440'
        }}>
          <div style={{ 
            fontSize: '11px', 
            color: '#ef4444',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            ⚠️ Data may be outdated - Use with caution
          </div>
        </div>
      )}

      {/* Freshness Indicator */}
      {!isStale && (
        <div style={{ 
          marginTop: '12px', 
          padding: '8px 12px', 
          backgroundColor: '#10b98120',
          borderRadius: '4px',
          border: '1px solid #10b98140'
        }}>
          <div style={{ 
            fontSize: '11px', 
            color: '#10b981',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            ✓ Data is fresh and reliable
          </div>
        </div>
      )}
    </div>
  );
}
